import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import DsIcon from "./DsIcon";
import { sidebarItemsFor } from "./navItems";
import useTheme from "../hooks/useTheme";
import { useRole, useAccess, useDeny } from "../auth/AuthContext";
import { AUTH_ENABLED, signOut } from "../auth/session";

/**
 * DsTopBar — 모바일 상단 바 (lg 미만).
 *
 * 로고 + 메뉴(☰). h-14 sticky, 그림자 0, 하단 border 만.
 * QCC-6: 하단 탭바 제거에 따라 nav 를 상단바 메뉴로 이동. 전체 라우트(SIDEBAR_ITEMS)
 *   제공 — 모바일에서 QCC·설정·단가DB 등으로 갇히지 않게.
 *   테마 전환은 설정(☰→설정→테마)으로 이동 — 상단바 토글 제거(공간·단순화).
 */
function isActive(pathname, path) {
  if (path === "/") return pathname === "/";
  return pathname === path || pathname.startsWith(path + "/");
}

export default function DsTopBar() {
  const [menuOpen, setMenuOpen] = useState(false);
  const { pathname } = useLocation();
  const { theme, toggleTheme } = useTheme();
  const role = useRole();
  const items = sidebarItemsFor(role, useAccess(), useDeny());

  useEffect(() => {
    if (!menuOpen) return undefined;
    const body = document.body;
    const root = document.documentElement;
    const previousBodyOverflow = body.style.overflow;
    const previousRootOverflow = root.style.overflow;
    const previousOverscroll = root.style.overscrollBehavior;
    body.style.overflow = "hidden";
    root.style.overflow = "hidden";
    root.style.overscrollBehavior = "none";
    const closeOnEscape = (event) => {
      if (event.key === "Escape") setMenuOpen(false);
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => {
      window.removeEventListener("keydown", closeOnEscape);
      body.style.overflow = previousBodyOverflow;
      root.style.overflow = previousRootOverflow;
      root.style.overscrollBehavior = previousOverscroll;
    };
  }, [menuOpen]);

  return (
    <header
      className="lg:hidden print:hidden sticky top-0 z-30 flex items-center justify-between px-4"
      style={{
        height: 56,
        background: "var(--ds-canvas-soft)",
        borderBottom: "1px solid var(--ds-line)",
      }}
    >
      <Link to="/" className="flex items-baseline gap-1.5" aria-label="대시보드" style={{ fontFamily: "var(--ff-brand)" }}>
        <span style={{ fontWeight: 700, fontSize: 14, color: "var(--ds-ink)", letterSpacing: "-0.03em" }}>
          ig
        </span>
        <span style={{ fontWeight: 500, fontSize: 14, color: "var(--ds-mute)", letterSpacing: "0.01em" }}>
          works
        </span>
      </Link>

      <button
        type="button"
        className="ds-topbar-menu-btn"
        aria-label="메뉴"
        aria-expanded={menuOpen}
        onClick={() => setMenuOpen((o) => !o)}
      >
        <svg width="20" height="20" viewBox="0 0 20 20" aria-hidden="true">
          <path d="M3 5.5h14M3 10h14M3 14.5h14" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
        </svg>
      </button>

      {menuOpen && (
        <>
          <button
            type="button"
            className="ds-topbar-menu-backdrop"
            aria-label="메뉴 닫기"
            onClick={() => setMenuOpen(false)}
          />
          <nav className="ds-topbar-menu" aria-label="메뉴">
            {items.map((item) => {
              // 임베드(SMBM): 본체=앱 내 라우트, ↗=새 탭 팝업.
              if (item.embed) {
                return (
                  <div key={item.key} className={`ds-topbar-menu-item${isActive(pathname, item.path) ? " is-active" : ""}`} style={{ paddingRight: 10 }}>
                    <Link to={item.path} onClick={() => setMenuOpen(false)}
                          style={{ display: "flex", alignItems: "center", gap: 12, flex: 1, color: "inherit", textDecoration: "none" }}>
                      <DsIcon name={item.icon} size={18} />
                      <span>{item.label}</span>
                    </Link>
                    <a href={item.url} target="_blank" rel="noopener noreferrer"
                       onClick={(e) => e.stopPropagation()} aria-label={`${item.label} 새 탭에서 열기`}
                       style={{ marginLeft: "auto", padding: "2px 6px", color: "inherit", opacity: 0.6, textDecoration: "none" }}>↗</a>
                  </div>
                );
              }
              if (item.external) {
                return (
                  <a key={item.key} href={item.url} target="_blank" rel="noopener noreferrer"
                     className="ds-topbar-menu-item" onClick={() => setMenuOpen(false)}>
                    <DsIcon name={item.icon} size={18} />
                    <span>{item.label} ↗</span>
                  </a>
                );
              }
              return (
                <Link key={item.key} to={item.path}
                      className={`ds-topbar-menu-item${isActive(pathname, item.path) ? " is-active" : ""}`}
                      onClick={() => setMenuOpen(false)}>
                  <DsIcon name={item.icon} size={18} />
                  <span>{item.label}</span>
                </Link>
              );
            })}
            {/* 테마 토글 — 항상 노출(설정 메뉴 숨겨지는 직원도 접근 가능하게 분리) */}
            <button
              type="button"
              className="ds-topbar-menu-item"
              onClick={() => toggleTheme()}
              style={{ width: "100%", textAlign: "left", background: "transparent", border: "none", cursor: "pointer", font: "inherit" }}
            >
              <svg width="18" height="18" viewBox="0 0 20 20" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.6">
                {theme === "dark" ? (
                  <>
                    <circle cx="10" cy="10" r="3.4" />
                    <path d="M10 2.5v2M10 15.5v2M2.5 10h2M15.5 10h2M4.7 4.7l1.4 1.4M13.9 13.9l1.4 1.4M15.3 4.7l-1.4 1.4M6.1 13.9l-1.4 1.4" strokeLinecap="round" />
                  </>
                ) : (
                  <path d="M16 11.5A6 6 0 1 1 8.5 4a4.6 4.6 0 0 0 7.5 7.5z" strokeLinejoin="round" />
                )}
              </svg>
              <span>{theme === "dark" ? "라이트 모드" : "다크 모드"}</span>
            </button>
            {AUTH_ENABLED && (
              <button
                type="button"
                className="ds-topbar-menu-item"
                onClick={() => { setMenuOpen(false); signOut(); }}
                style={{ width: "100%", textAlign: "left", background: "transparent", border: "none", cursor: "pointer", font: "inherit" }}
              >
                <DsIcon name="logout" size={18} />
                <span>로그아웃</span>
              </button>
            )}
          </nav>
        </>
      )}
    </header>
  );
}
